import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faBookmark as faBookmarkSolid,
  faClipboardList,
  faLocationDot,
  faTag,
} from '@fortawesome/free-solid-svg-icons';
import { faBookmark as faBookmarkRegular } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ResultOf } from '@graphql-typed-document-node/core';
import classNames from 'classnames';
import { RawDraftContentState } from 'draft-js';
import {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionFunctionArgs,
  useFetcher,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { client } from '~/client';
import { FragmentType, getFragmentData, graphql } from '~/gql';
import { Activity, Tag } from '~/gql/graphql';
import { formatDate } from '~/helpers/formatDate';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { ReadOnlyEditor } from '../form/editor/ReadOnlyEditor';
import { TaskBadge } from '../job/JobView/Feed/Feed';
import { MemoGallery } from '../job/JobView/Feed/Gallery';
import { TagsBuilder } from '../TagCombobox/TagsBuilder';
import { WrappingTags } from '../TagCombobox/WrappingTags';
import { Avatar } from '../ui/Avatar';
import { Card } from '../ui/nucleus';
import { StatusBadge } from '../ui/nucleus/StatusBadge';
import AddJobIcon from './icons/AddJobIcon';

type Props = {
  activity: FragmentType<typeof ActivityListItem_Activity>;
  onTag: (activity: Activity) => void;
  onTagClick?: (tag: Tag) => void;
  onSelect: (id: string, isSingle: boolean) => void;
  onClick: () => void;
  showCreateJobFrom?: boolean;
};

const ActivityListItem_TagFragment = graphql(/* GraphQL */ `
  fragment ActivityListItem_TagFragment on Tag {
    id
    entityType
    category
    name
    description
    colour
  }
`);

export const ActivityListItem_Activity = graphql(`
  fragment ActivityListItem_ActivityFragment on Activity {
    id
    kind
    createdAt
    isRead
    isFlagged
    tags {
      ...ActivityListItem_TagFragment
    }
    createdBy {
      id
      name
      image
    }
    job {
      id
      name
      site {
        id
        name
        image
        address
      }
      jobTags {
        ...ActivityListItem_TagFragment
      }
    }
    task {
      id
      name
      notes
      tags {
        nodes {
          ...ActivityListItem_TagFragment
        }
      }
      attachments {
        id
        kind
        thumb
        url
        originalFilename
      }
    }
    comment {
      id
      body
      attachments {
        id
        kind
        thumb
        url
        originalFilename
      }
      tags {
        ...ActivityListItem_TagFragment
      }
    }
    entry {
      id
      status
      inputDate
    }
  }
`);

function Tags(props: {
  tags: FragmentType<typeof ActivityListItem_TagFragment>[];
}) {
  const tags = getFragmentData(ActivityListItem_TagFragment, props.tags);

  return <TagsBuilder tags={[...tags]} />;
}

/**
 * Memoized ActivityListItem to prevent re-renders when parent state changes.
 * Only re-renders when props actually change (activity data, callbacks).
 */
export const ActivityListItem = memo(function ActivityListItem(props: Props) {
  // Destructure callbacks to use as stable dependency references instead of entire props object
  const { onTag, onTagClick, onSelect, onClick } = props;
  const fetcher = useFetcher();
  const activity = getFragmentData(ActivityListItem_Activity, props.activity);
  const [searchParams] = useSearchParams();
  const isSelected = searchParams.get('activity') === activity.id;
  const navigate = useNavigate();
  const location = useLocation();

  const [isRead, setIsRead] = useState(activity.isRead);

  // Effect to sync read state from sessionStorage
  // Using activity.id and activity.isRead as dependencies instead of full activity object
  // to prevent unnecessary effect runs when parent re-renders with same data
  useEffect(() => {
    const lsList =
      window.sessionStorage.getItem('activity.recentlyReadActivities') ?? '[]';
    const recentlyReadActivities = JSON.parse(lsList);

    if (recentlyReadActivities.includes(activity.id)) {
      setIsRead(true);
    } else {
      setIsRead(activity.isRead);
    }
  }, [activity.id, activity.isRead]);

  const addIdToStorage = useCallback((id: string) => {
    const lsList =
      window.sessionStorage.getItem('activity.recentlyReadActivities') ?? '[]';
    const list: Array<string> = JSON.parse(lsList);
    list.push(id);
    window.sessionStorage.setItem(
      'activity.recentlyReadActivities',
      JSON.stringify(list)
    );
  }, []);

  const handleClick = useCallback(() => {
    const to =
      activity.entry && activity.task
        ? `jobs/${activity.job?.id}/tasks/${activity.task.id}`
        : `jobs/${activity.job?.id}?activity=${activity.id}${
            activity.kind === 'message' || activity.task ? '#messages-open' : ''
          }`;

    const returnTo = `${location.pathname}${location.search}`;
    navigate(to, { state: { returnTo } });
    // Adding a 500ms delay to allow for any dependent logic or UI updates to complete
    setTimeout(() => onSelect(activity.id, true), 500);

    if (isRead) return;

    fetcher.submit(
      { id: activity.id },
      { method: 'post', action: '/activity/mark-as-read' }
    );

    addIdToStorage(activity.id);
    setIsRead(true);
    onClick();
    // Dependency: using destructured onSelect/onClick instead of entire props object
    // to prevent unnecessary recreations when other props change
  }, [
    activity.entry,
    activity.task,
    activity.job?.id,
    activity.id,
    activity.kind,
    location.pathname,
    location.search,
    navigate,
    onSelect,
    onClick,
    isRead,
    fetcher,
    addIdToStorage,
  ]);

  // Memoize onTag callback to prevent ActivityListCard re-renders
  // Dependency: destructured onTag (not entire props object) to avoid unstable references
  const handleTag = useCallback(() => {
    onTag(activity as Activity);
  }, [onTag, activity]);

  // Memoize onTagClick callback
  const handleTagClick = useCallback(
    (tag: Tag) => {
      onTagClick?.(tag);
    },
    [onTagClick]
  );

  /**
   * Holds an "optimistic update" version of the activity - I think?
   * There may be problems here if something causes the data to drift out of sync
   */

  return (
    <div className='min-w-0 flex-1' onClick={handleClick}>
      <ActivityListCard
        activity={activity}
        isRead={isRead}
        isSelected={isSelected}
        onTag={handleTag}
        onTagClick={onTagClick ? handleTagClick : undefined}
        showCreateJobFrom={props.showCreateJobFrom}
      />
    </div>
  );
});

type ActivityListCardProps = {
  activity: ResultOf<typeof ActivityListItem_Activity>;
  isRead: boolean;
  isSelected: boolean;
  onTag: () => void;
  onTagClick?: (tag: Tag) => void;
  showCreateJobFrom?: boolean;
};

/**
 * Memoized ActivityListCard to prevent re-renders when parent state changes.
 * Uses shallow comparison of props to determine if re-render is needed.
 */
export const ActivityListCard = memo(function ActivityListCard(
  props: ActivityListCardProps
) {
  const navigate = useNavigate();
  const { activity, isRead, isSelected, onTag, onTagClick, showCreateJobFrom } =
    props;
  const parsedCommentBody = useMemo(() => {
    const body = activity.comment?.body;
    if (!body) return null;
    try {
      const parsed = JSON.parse(body) as RawDraftContentState;
      return parsed?.blocks ? parsed : null;
    } catch {
      return null;
    }
  }, [activity.comment?.body]);

  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const [showFlagButton, setShowFlagButton] = useState(false);

  const user = activity.createdBy;
  const site = activity.job?.site;

  // used for optimistic updates:
  const [flagged, setFlagged] = useState(activity.isFlagged);

  useEffect(() => setFlagged(activity.isFlagged), [activity.isFlagged]);

  // Check if this is an assignee note activity
  // Assignee notes now return kind: "assigneeNotes" (distinct from task notes which are kind: "notes")
  const isAssigneeNote = useMemo(() => {
    return activity.kind === 'assigneeNotes';
  }, [activity.kind]);

  // Parse assignee notes content if available
  // For assignee notes (kind: "assigneeNotes"), the content should be in comment.body (like regular messages)
  const parsedAssigneeNotes = useMemo(() => {
    if (!isAssigneeNote) return null;
    const assigneeNotesContent = activity.comment?.body;
    if (!assigneeNotesContent) {
      return null;
    }
    try {
      const parsed = JSON.parse(assigneeNotesContent) as RawDraftContentState;
      return parsed?.blocks ? parsed : null;
    } catch {
      // If parsing fails, it might be plain text
      return null;
    }
  }, [isAssigneeNote, activity.comment?.body]);

  let description: ReactNode = null;
  // Handle assignee notes, task notes, comments, and messages
  if (activity.comment || activity.task || isAssigneeNote) {
    // For assignee notes, the content should be in comment.body (backend should populate this)
    // For other activities, use comment.body or task.notes
    const body = isAssigneeNote
      ? activity.comment?.body
      : activity.comment?.body || activity.task?.notes;

    // Render the container if it's an assignee note with content or attachments
    if (isAssigneeNote) {
      const hasContent = parsedAssigneeNotes || body;
      const hasAttachments = activity.comment?.attachments?.length;

      // Only render if there's content or attachments
      if (hasContent || hasAttachments) {
        description = (
          <div
            className={classNames(
              'mb-2 flex w-full flex-col items-start justify-center gap-2 border-l-2 border-status-complete bg-status-complete/5 px-3.5 py-2.5 lg:w-3/4'
            )}
          >
            {parsedAssigneeNotes ? (
              <ReadOnlyEditor content={parsedAssigneeNotes} />
            ) : body ? (
              <p className='text-sm text-primary'>{body}</p>
            ) : null}
            {activity.comment?.attachments &&
              activity.comment.attachments.length > 0 && (
                <div>
                  <MemoGallery attachments={activity.comment.attachments} />
                </div>
              )}
          </div>
        );
      }
    } else if (body != null) {
      // Handle task notes and regular comments/messages when body is not null
      description = activity.task ? (
        <div
          className={classNames(
            'mb-2 flex w-full flex-col items-start justify-center gap-2 border-l-2 border-brand bg-surface-container-low px-3.5 py-2.5 lg:w-3/4',
            showFlagButton && 'bg-surface-container-bright' // Add hover effect conditionally
          )}
        >
          {activity.entry && activity.task && (
            <>
              {activity.task.notes && <p>{activity.task.notes}</p>}
              {activity.task.attachments && (
                <div>
                  <MemoGallery attachments={activity.task.attachments} />
                </div>
              )}
              <Tags tags={activity.task.tags.nodes} />
              <TaskBadge
                className={showFlagButton && 'bg-[#DDDEE0]'}
                name={activity.task.name}
              />
            </>
          )}
        </div>
      ) : (
        <>
          {parsedCommentBody ? (
            <ReadOnlyEditor content={parsedCommentBody} />
          ) : (
            body
          )}

          {activity.comment?.tags && (
            <TagsBuilder
              className='mt-1 pr-2'
              limit={isMobile ? 1 : 2}
              tags={activity.comment?.tags as Tag[]}
            />
          )}
        </>
      );
    } else {
      // Fallback for activities with no body content
      description = (
        <>
          <span>Attachment added</span>

          {activity.comment?.tags && (
            <TagsBuilder
              className='px-2'
              limit={isMobile ? 1 : 2}
              tags={activity.comment?.tags as Tag[]}
            />
          )}
        </>
      );
    }
  }

  return (
    <Card
      selected={isSelected}
      onMouseOver={() => setShowFlagButton(true)}
      onMouseOut={() => setShowFlagButton(false)}
      type={'activity'}
    >
      {showFlagButton && (
        <div className='absolute -top-3 right-2.5 flex'>
          {/* {activity.kind !== 'message' && ( */}
          <div
            className={classNames(
              'flex items-center justify-center overflow-hidden rounded-l border border-r-0 bg-white p-[2px] text-grey-40 transition-all'
            )}
            onClick={(e) => {
              e.stopPropagation();
              onTag();
            }}
          >
            <FontAwesomeIcon
              className='cursor-pointer rounded-l-sm px-[11px] py-[11px] transition-all hover:bg-grey-5 hover:text-primary'
              icon={faTag}
            />
          </div>

          <div
            className={classNames(
              'flex items-center justify-center overflow-hidden border bg-white px-[2px] transition-all',
              flagged ? 'text-flagged' : 'text-grey-40',
              // activity.kind !== 'message' ? 'rounded-r border-l-0' : 'rounded'
              'border-x-0'
            )}
            onClick={async (event) => {
              event.stopPropagation();
              setShowFlagButton(false);
              setFlagged(!flagged);
              await client.mutation(FlagMessage_Mutation, {
                id: activity.id,
                flagged: !flagged,
              });
            }}
          >
            <FontAwesomeIcon
              className={classNames(
                'cursor-pointer px-[11px] py-[11px] transition-all hover:bg-grey-5',
                flagged ? 'text-flagged' : 'hover:text-primary'
                // activity.kind !== 'message' ? 'rounded-r-sm' : 'rounded-sm'
              )}
              icon={(flagged ? faBookmarkSolid : faBookmarkRegular) as IconProp}
            />
          </div>

          {showCreateJobFrom && (
            <div
              className={classNames(
                'flex items-center justify-center overflow-hidden border bg-white px-[2px] text-grey-40 transition-all',
                'rounded-r border-l-0'
              )}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`jobs/new?activity=${activity.id}`);
              }}
            >
              <AddJobIcon
                className='cursor-pointer rounded-r-sm p-0.5 hover:bg-grey-5 hover:text-primary'
                width={34}
                height={38}
              />
            </div>
          )}
        </div>
      )}
      <div className='flex items-start gap-2 lg:gap-3'>
        <div className='flex items-center gap-1 pt-1 lg:gap-2'>
          <div className='my-auto -ml-1 flex w-3 items-center justify-center lg:ml-0'>
            {!isRead && (
              <div className='h-[9px] w-[9px] rounded-full bg-blue'></div>
            )}
          </div>
          <div className='ml-1 hidden shrink-0 items-stretch overflow-hidden rounded-md xs:block'>
            <Avatar
              size='large'
              automation={user ? false : true}
              image={user?.image}
              name={user?.name}
            />
          </div>
        </div>
        <div className='min-w-0 flex-1'>
          <div
            className={classNames(
              'flex items-center gap-3 text-sm font-semibold lg:text-base'
            )}
          >
            <div className='flex min-w-0 items-baseline gap-1.5'>
              <span className='pb-0.5 font-semibold text-primary'>
                {user?.name ?? t('automation')}
              </span>
              {activity.entry && !activity.task && (
                <>
                  <StatusBadge
                    usedIn={'activityFeed'}
                    status={activity.entry.status}
                  />
                  <span className='whitespace-nowrap pb-0.5 font-normal text-secondary'>
                    job
                  </span>
                </>
              )}
              <div className='ml-1 whitespace-nowrap text-2xs text-tertiary'>
                {formatDate(activity.createdAt, 'hh:mm a')}
              </div>
            </div>
          </div>
          {description && (
            <div className='mb-1.5 break-words text-sm md:text-base'>
              {description}
            </div>
          )}

          {/* Don't render attachments here for assignee notes - they're already in the container */}
          {activity.comment?.attachments?.length && !isAssigneeNote && (
            <MemoGallery attachments={activity.comment.attachments} />
          )}

          {/* {activity.task?.attachments?.length && (
            <MemoGallery attachments={activity.task.attachments} />
          )} */}

          <div className='mt-0.5 flex flex-col items-start gap-1 text-sm font-normal text-tertiary'>
            {/* Job info row - on mobile: stack vertically, on desktop: inline with tags */}
            <div
              className={classNames(
                'flex w-full gap-1.5',
                isMobile ? 'flex-col items-start' : 'items-start'
              )}
            >
              {/* Job name row */}
              <div className='flex min-w-0 flex-wrap items-start gap-x-1.5'>
                <FontAwesomeIcon
                  className='shrink-0 pt-[3px]'
                  icon={faClipboardList}
                />
                <p className='min-w-0 truncate'>{`${activity.job?.id} - ${activity.job?.name}`}</p>
                {activity.task && (
                  <p className='min-w-0 truncate'>
                    {` / ${activity.task.name}`}
                  </p>
                )}
              </div>

              {/* Job tags - on mobile: below job name, on desktop: inline */}
              {activity.job?.jobTags && activity.job.jobTags.length > 0 && (
                <div
                  className={classNames(
                    'min-w-0',
                    isMobile ? 'w-full pl-5' : 'flex-1'
                  )}
                >
                  <WrappingTags
                    tags={activity.job.jobTags as Tag[]}
                    onClick={onTagClick}
                    miniTag
                  />
                </div>
              )}
            </div>

            {/* Site/place row - always below job info */}
            {site && (
              <div className='flex min-w-0 items-start gap-1.5'>
                <FontAwesomeIcon
                  className='shrink-0 pt-[3px]'
                  icon={faLocationDot}
                />
                <span className='min-w-0 truncate'>{site.name}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className='absolute right-[1.5rem] top-[-0.27rem] lg:top-[-0.313rem]'>
        {flagged && !showFlagButton && (
          <FontAwesomeIcon
            icon={faBookmarkSolid}
            className='h-4 text-flagged transition-all'
          />
        )}
      </div>

      {/* Activity tags at the bottom */}
      {activity.tags && activity.tags.length > 0 && (
        <div className={classNames('mt-2', isMobile ? 'ml-14' : 'ml-18')}>
          <WrappingTags tags={activity.tags as Tag[]} />
        </div>
      )}
    </Card>
  );
});

//
// ----------------------------------------------------------------------------
//

const FlagMessage_Mutation = graphql(/* GraphQL */ `
  mutation FlagMessage($id: ID!, $flagged: Boolean!) {
    flagMessage(id: $id, flagged: $flagged)
  }
`);

const MarkAsRead_Mutation = graphql(/* GraphQL */ `
  mutation MarkAsRead($id: ID!) {
    markAsRead(id: $id)
  }
`);

export async function markAsRead({ request }: ActionFunctionArgs) {
  const data = await request.formData();
  const id = data.get('id');

  if (!(id && typeof id === 'string')) {
    return null;
  }

  await client.mutation(MarkAsRead_Mutation, { id }).toPromise();

  return null;
}
